import { BaseStore } from 'fluxible/addons';
import castEmptyArrayToObject from 'contest/utils/cast-empty-array-to-object';

export default class BriefStore extends BaseStore {
    constructor(dispatcher) {
        super(dispatcher);

        this.listingId = null;
        this.outline = {};
        this.changes = {};
        this.score = {};
        this.isEditable = false;
        this.saveBriefElementUrl = '';
        this.createBriefFileUrl = '';

        this.languages = [];
        this.isTranslatable = false;
        this.translationUrl = null;
        this.translationInProgress = false;
        this.translationFailed = false;
        this.selectedLanguageKey = null;

        this.signatureEndpoint = '';
        this.s3Endpoint = '';
        this.s3AccessKey = '';
        this.s3Bucket = '';

        this.uploadSizeLimit = null;

        this.deliverables = [];

        this.uploads = {}; // { "section/element": [ uploadid, .. ] }
        this.uploadsMetadata = {}; // { uploadid: { thumb, name, size } }
    }

    load(payload) {
        this.listingId = payload.listingId;
        this.outline = payload.outline;
        this.changes = castEmptyArrayToObject(payload.changes);
        this.score = payload.score || {};
        this.isEditable = payload.isEditable;
        this.saveBriefElementUrl = payload.saveBriefElementUrl;
        this.createBriefFileUrl = payload.createBriefFileUrl;

        this.languages = payload.languages;
        this.deliverables = payload.deliverables;
        this.fileTypes = payload.fileTypes;
        this.isTranslatable = payload.isTranslatable && !this.isEditable; // google translation + autosave = disaster
        this.translationUrl = payload.translationUrl;
        this.selectedLanguageKey = payload.selectedLanguageKey;

        this.signatureEndpoint = payload.signatureEndpoint;
        this.s3Endpoint = payload.s3Endpoint;
        this.s3AccessKey = payload.s3AccessKey;
        this.s3Bucket = payload.s3Bucket;

        this.uploadSizeLimit = payload.uploadSizeLimit;

        this.emitChange();
    }

    getIndustry() {
        return (
            this.outline.backgroundInformation &&
            this.outline.backgroundInformation.elements.industry &&
            this.outline.backgroundInformation.elements.industry.value
        );
    }

    getIndustryName() {
        return (
            this.outline.backgroundInformation &&
            this.outline.backgroundInformation.elements.industry &&
            this.outline.backgroundInformation.elements.industry.trans.choices[this.getIndustry()]
        );
    }

    elementUpdateElement(payload) {
        this._updateOutlineElement(payload.section, payload.element, {
            value: payload.value,
        });

        this.emitChange();
    }

    elementSaveStart(payload) {
        this._updateOutlineElement(payload.section, payload.element, {
            saveState: 'saving',
        });

        this.emitChange();
    }

    elementSaveSuccess(payload) {
        this._updateOutlineElement(payload.section, payload.element, {
            saveState: 'saved',
        });
        this.score = payload.score;

        this.emitChange();
    }

    elementSaveError(payload) {
        this._updateOutlineElement(payload.section, payload.element, {
            saveState: 'error',
        });

        this.emitChange();
    }

    _updateOutlineElement(sectionKey, elementKey, obj) {
        // Update brief element
        // .. but avoid mutating outline to avoid repainting the whole app
        this.outline[sectionKey].elements[elementKey] = {
            ...this.outline[sectionKey].elements[elementKey],
            ...obj,
        };
    }

    translationStart(payload) {
        this.selectedLanguageKey = payload.language;
        this.translationInProgress = true;
        this.translationFailed = false;
        this.emitChange();
    }

    translationSuccess(payload) {
        this.outline = payload.outline;
        this.translationInProgress = false;
        this.emitChange();
    }

    translationError(payload) {
        this.translationInProgress = false;
        this.translationFailed = true;
        this.emitChange();
    }

    addUpload(payload) {
        this.uploadsMetadata[payload.path] = this.uploadsMetadata[payload.path] || {};
        this.uploadsMetadata[payload.path][payload.uploadid] = payload;
        this.uploads[payload.path] || (this.uploads[payload.path] = []);
        this.uploads[payload.path].push(payload.uploadid);

        this.emitChange();
    }

    removeUpload(payload) {
        this.uploads[payload.path] = this.uploads[payload.path].filter(uploadid => {
            return payload.uploadid !== uploadid;
        });

        this.emitChange();
    }

    updateUpload(payload) {
        this.uploadsMetadata[payload.path] = this.uploadsMetadata[payload.path] || {};
        this.uploadsMetadata[payload.path][payload.uploadid] = Object.assign(
            {},
            this.uploadsMetadata[payload.path][payload.uploadid],
            payload
        );
        this.emitChange();
    }

    getNextScoringElement() {
        if (this.score.nextElement) {
            const nextElement = this.score.nextElement;
            const outlineElement = this.outline[nextElement.sectionKey]['elements'][
                nextElement.elementKey
            ];
            return {
                sectionKey: nextElement.sectionKey,
                elementKey: nextElement.elementKey,
                title: outlineElement.trans.title,
                type: outlineElement.type,
            };
        }

        this.nextScoringElement;
    }
}
BriefStore.storeName = 'BriefStore';
BriefStore.handlers = {
    LOAD_BRIEF: 'load',
    BRIEF_UPDATE_ELEMENT: 'elementUpdateElement',

    BRIEF_SAVE_START: 'elementSaveStart',
    BRIEF_SAVE_SUCCESS: 'elementSaveSuccess',
    BRIEF_SAVE_ERROR: 'elementSaveError',

    BRIEF_TRANSLATION_START: 'translationStart',
    BRIEF_TRANSLATION_SUCCESS: 'translationSuccess',
    BRIEF_TRANSLATION_ERROR: 'translationError',

    BRIEF_ADD_UPLOAD: 'addUpload',
    BRIEF_REMOVE_UPLOAD: 'removeUpload',
    BRIEF_UPDATE_UPLOAD: 'updateUpload',
};



// WEBPACK FOOTER //
// ./src/web/js/contest/stores/brief-store.jsx